/**
 * Parsers for a UPS create-shipment response. Shared so both the interactive builder and the
 * server/release-time processing path read the response the same way (one source of truth for
 * UPS's field-name quirks).
 */

const trimString = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();

/**
 * UPS label ZPL arrives with sticky printer-config commands in its header: ^POI (print inverted
 * 180°) and ^LH10,12 (shifted label home). ZPL keeps those settings for EVERY later label until
 * something changes them or the printer power-cycles — so printing one UPS label flipped all
 * subsequent labels (packing slips etc.) upside down. Rewrite them to the defaults (^PON, ^LH0,0)
 * once here, so the stored label is clean and no print-time fixup is ever needed.
 * scripts/appwrite-migrations/backfill-ups-label-zpl-sanitize.mjs carries an exact copy of this
 * function for the already-stored labels — keep the two in sync.
 */
export function sanitizeUpsLabelZpl(zpl: string): string {
    return zpl
        .replace(/\^POI/g, '^PON')
        .replace(/\^LH\d+,\d+/g, '^LH0,0');
}

/**
 * Pull each package's tracking number + decoded ZPL out of a UPS create-shipment response.
 * PackageResults is an array for multi-package shipments and a single object for one package; its
 * order matches the Package array we sent, so index i ↔ packages[i]. GraphicImage is base64 ZPL.
 */
export function parseUpsPackageResults(data: unknown): { trackingNumber: string; labelZpl: string }[] {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const results: any = (data as any)?.ShipmentResponse?.ShipmentResults ?? (data as any)?.ShipmentResults;
    const pkgResults = results?.PackageResults;
    if (!pkgResults) return [];
    const list = Array.isArray(pkgResults) ? pkgResults : [pkgResults];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return list.map((pr: any) => {
        const graphicImage: string = pr?.ShippingLabel?.GraphicImage ?? '';
        return {
            trackingNumber: trimString(pr?.TrackingNumber),
            labelZpl: graphicImage ? sanitizeUpsLabelZpl(atob(graphicImage)) : '',
        };
    });
}

/**
 * Deep-search a nested object for the first string value under `key` (used to pull the
 * ShipmentIdentificationNumber out of the UPS shipment response).
 */
export function findUpsResponseValue(obj: unknown, key: string): string | null {
    if (!obj || typeof obj !== 'object') return null;
    for (const [k, val] of Object.entries(obj as Record<string, unknown>)) {
        if (k === key && typeof val === 'string') return val;
        if (val && typeof val === 'object') {
            const found = findUpsResponseValue(val, key);
            if (found) return found;
        }
    }
    return null;
}
